import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
import { Phone, ArrowLeft, MapPin } from 'lucide-react';
import { Navigation } from './Navigation';
interface ServicePageLayoutProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  metaDescription: string;
  schema: object;
}
export function ServicePageLayout({
  title,
  subtitle,
  children,
  metaDescription,
  schema
}: ServicePageLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${title} | Mike's Pro Detail`;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', metaDescription);
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify(schema);
    document.head.appendChild(schemaScript);
    return () => {
      if (document.head.contains(schemaScript)) {
        document.head.removeChild(schemaScript);
      }
    };
  }, [title, metaDescription, schema]);
  return <main className="min-h-screen w-full bg-black text-white selection:bg-red-600 selection:text-white" data-id="element-282">
      <Navigation data-id="element-283" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-4 border-b border-zinc-900 overflow-hidden" data-id="element-284">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-red-900/20 blur-[120px] pointer-events-none rounded-full" data-id="element-285" />

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-286">
          <a href="/" className="inline-flex items-center text-gray-400 hover:text-white mb-8 transition-colors group" data-id="element-287">
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-288" />
            Back to Home
          </a>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-289">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight mb-6" data-id="element-290">
              <span className="bg-clip-text text-transparent bg-gradient-to-b from-yellow-300 via-yellow-400 to-yellow-500 drop-shadow-lg" data-id="element-291">
                {title}
              </span>
            </h1>
            <div className="h-1 w-24 bg-red-600 rounded-full shadow-[0_0_10px_rgba(220,38,38,0.8)] mb-6" data-id="element-292" />
            <p className="text-xl text-gray-400 leading-relaxed max-w-3xl" data-id="element-293">
              {subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Area */}
      <section className="py-16 px-4" data-id="element-294">
        <div className="max-w-4xl mx-auto" data-id="element-295">
          <div className="prose prose-invert prose-lg max-w-none prose-headings:font-black prose-headings:uppercase prose-headings:tracking-tight prose-h2:text-3xl prose-h2:mb-6 prose-h2:mt-12 prose-h3:text-2xl prose-h3:text-yellow-400 prose-p:text-gray-300 prose-p:leading-relaxed prose-li:text-gray-300 prose-strong:text-white" data-id="element-296">
            {children}
          </div>
        </div>
      </section>

      {/* Service Areas SEO Block */}
      <section className="py-12 px-4 bg-zinc-900/30 border-t border-zinc-900" data-id="element-297">
        <div className="max-w-4xl mx-auto text-center" data-id="element-298">
          <MapPin className="w-8 h-8 text-red-500 mx-auto mb-4" data-id="element-299" />
          <h2 className="text-2xl font-bold mb-4" data-id="element-300">
            Proudly Serving Los Angeles & Ventura County
          </h2>
          <p className="text-gray-400 leading-relaxed" data-id="element-301">
            We provide top-tier mobile auto detailing in Camarillo CA, Thousand
            Oaks, Agoura Hills, Calabasas, Woodland Hills, Westlake Village,
            Encino, Tarzana, Simi Valley, Moorpark, Chatsworth, Malibu, and
            throughout Ventura County. Whether you need car detailing in
            Camarillo or mobile detailing in Ventura County, we come directly to
            your home or office.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden border-t border-zinc-900" data-id="element-302">
        <div className="absolute inset-0 bg-gradient-to-br from-red-900/40 to-black pointer-events-none" data-id="element-303" />
        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-304">
          <h2 className="text-4xl md:text-5xl font-black uppercase mb-6" data-id="element-305">
            Ready to Transform Your Vehicle?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto" data-id="element-306">
            Book your professional mobile detailing appointment today. We bring
            the shop to you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-307">
            <a href="tel:8183819905" className="flex items-center gap-3 px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-bold text-xl rounded-sm transition-all shadow-[0_0_20px_rgba(220,38,38,0.5)] hover:shadow-[0_0_30px_rgba(220,38,38,0.8)] hover:-translate-y-1 w-full sm:w-auto justify-center" data-id="element-308">
              <Phone className="w-6 h-6" data-id="element-309" />
              (818) 381-9905
            </a>
            <a href="/#contact" className="px-8 py-4 bg-white text-black hover:bg-gray-200 font-bold text-xl uppercase tracking-wider rounded-sm transition-all w-full sm:w-auto text-center" data-id="element-310">
              Book Online
            </a>
          </div>
        </div>
      </section>
    </main>;
}